import {
  Area,
  AreaChart,
  Bar,
  BarChart,
  Cell,
  Line,
  LineChart,
  ReferenceLine,
  XAxis,
  YAxis,
} from 'recharts';
import { useStore } from '@/state/store';
import { project } from '@/engine';
import { generateNarrative } from '@/content/narrative';
import { fmtCurrency, fmtCurrencyCompact, fmtPct, fmtRatioPct } from '@/lib/format';
import type { MonthlyRow, ScenarioInputs } from '@/types';
import { COLORS, downsample, yearAxisProps } from '@/components/charts/chartUtils';

const W = 680;
const H = 200;
const axis = { stroke: '#333', fontSize: 9 };

function assumptionRows(inputs: ScenarioInputs): [string, string][] {
  return [
    ['Portfolio value', fmtCurrency(inputs.portfolioValue)],
    ['Blended advance rate', fmtRatioPct(inputs.blendedAdvanceRate)],
    ['Minimum loan value', fmtCurrency(inputs.minimumLoanValue)],
    ['Expected return', fmtPct(inputs.portfolioExpectedReturnAnnualPct)],
    ['Draw', `${fmtCurrency(inputs.drawAmount)} / ${inputs.drawFrequency}`],
    ['Payment mode', inputs.paymentMode],
    ['Base rate', fmtPct(inputs.ratePath.baseRatePct)],
    ['Spread', fmtPct(inputs.spreadPct)],
    ['Rate path', inputs.ratePath.type],
    ['Horizon', `${inputs.years} years`],
    ['Cost basis %', `${inputs.costBasisPct}%`],
    [
      'Cap-gains model',
      inputs.tax.useBracketedCapGains
        ? `Brackets (${inputs.tax.filingStatus.toUpperCase()})`
        : `Flat ${fmtPct(inputs.tax.longTermCapGainsRatePct)}`,
    ],
    ['Cap-gains rate', fmtPct(inputs.tax.longTermCapGainsRatePct + inputs.tax.stateCapGainsRatePct)],
    ['Forced liquidation', inputs.modelForcedLiquidation ? `on (cure LTV ${fmtRatioPct(inputs.cureTargetLtv)})` : 'off'],
  ];
}

/**
 * Dedicated print-report view (§10.10). Hidden on screen, shown only when printing. Charts
 * use explicit width/height (not ResponsiveContainer, which mis-measures while printing).
 */
export function PrintReport() {
  const inputs = useStore((s) => s.inputs);
  const result = useStore((s) => s.result);
  const name = useStore((s) => s.currentName);
  const sofrDate = useStore((s) => s.sofrLatestDate);
  const mcResult = useStore((s) => s.mcResult);
  const selection = useStore((s) => s.ui.comparisonSelection);
  const scenarios = useStore((s) => s.scenarios);

  if (!result) return <div className="print-only" />;

  const isComparison = selection.length >= 2;
  const narrative = generateNarrative(result, inputs);
  const rows = result.rows;

  const networth = downsample(rows).map((r) => ({ year: +(r.monthIndex / 12).toFixed(1), portfolio: r.portfolioValue, loan: r.endBalance, netWorth: r.netWorth }));
  const ltv = downsample(rows).map((r) => ({ year: +(r.monthIndex / 12).toFixed(1), ltv: Number.isFinite(r.ltv) ? +(r.ltv * 100).toFixed(1) : null }));
  const sellByMonth = new Map(result.sellPath.rows.map((row) => [row.monthIndex, row.netWorth]));
  const bvs = downsample(rows).map((r) => ({ year: +(r.monthIndex / 12).toFixed(1), borrow: r.netWorth, sell: sellByMonth.get(r.monthIndex) ?? 0 }));
  const cumulativeInterest = downsample(rows).map((r) => ({ year: +(r.monthIndex / 12).toFixed(1), total: r.cumulativeInterest }));
  const estateData = [
    { name: 'Borrow', value: result.estate.borrowHeirsValue },
    { name: 'Sell', value: result.estate.sellHeirsValue },
  ];

  return (
    <div className="print-only print-report">
      <header className="print-section">
        <h1>Securities-Backed Borrowing Report</h1>
        <div>
          <strong>{isComparison ? 'Comparison' : name}</strong> · Generated {new Date().toISOString().slice(0, 10)}
        </div>
        <div>SOFR as of {sofrDate}</div>
        <p className="print-disclaimer">
          For modeling and education only — not financial, investment, tax, or legal advice. A securities-backed line
          is a demand loan your lender can call at any time. Rates and terms are your own assumptions, not quotes.
          Tax rates and brackets are held at today's law for the entire projection: real rates, thresholds, and the
          step-up and estate rules will change, and those changes are the largest risk this model does not capture.
        </p>
      </header>

      {isComparison ? (
        <ComparisonSection selection={selection} scenarios={scenarios} />
      ) : (
        <>
          <section className="print-section">
            <h2>Assumptions</h2>
            <table>
              <tbody>
                {assumptionRows(inputs).map(([k, v]) => (
                  <tr key={k}><td>{k}</td><td>{v}</td></tr>
                ))}
              </tbody>
            </table>
          </section>

          <section className="print-section">
            <h2>Summary</h2>
            <div className="print-kpis">
              <div className="kpi-card">Starting APR<br /><strong>{fmtPct(result.summary.startApr)}</strong></div>
              <div className="kpi-card">Ending net worth<br /><strong>{fmtCurrency(result.summary.endingNetWorth)}</strong></div>
              <div className="kpi-card">Total interest<br /><strong>{fmtCurrency(result.summary.totalInterest)}</strong></div>
              <div className="kpi-card">Worst LTV<br /><strong>{fmtRatioPct(result.summary.worstLtv)}</strong></div>
              <div className="kpi-card">Min cushion<br /><strong>{fmtRatioPct(result.summary.minCallCushionPct)}</strong></div>
              <div className="kpi-card">First call<br /><strong>{result.summary.firstMaintenanceCallMonth ?? 'None'}</strong></div>
              <div className="kpi-card">Break-even<br /><strong>{result.summary.carryIsAccretive ? 'Accretive' : 'Erodes'}</strong></div>
              <div className="kpi-card">Estate difference<br /><strong>{fmtCurrency(result.estate.difference)}</strong></div>
            </div>
          </section>

          <section className="print-section">
            <h2>What this means</h2>
            {narrative.paragraphs.map((p, i) => <p key={i}>{p}</p>)}
          </section>

          <section className="chart-block">
            <h2>Net worth</h2>
            <LineChart width={W} height={H} data={networth}>
              <XAxis {...axis} {...yearAxisProps(networth)} /><YAxis {...axis} tickFormatter={fmtCurrencyCompact} width={50} />
              <Line dataKey="portfolio" stroke={COLORS.portfolio} dot={false} isAnimationActive={false} />
              <Line dataKey="loan" stroke={COLORS.loan} dot={false} isAnimationActive={false} />
              <Line dataKey="netWorth" stroke={COLORS.netWorth} strokeWidth={2} dot={false} isAnimationActive={false} />
            </LineChart>
          </section>

          <section className="chart-block">
            <h2>Borrow vs sell</h2>
            <LineChart width={W} height={H} data={bvs}>
              <XAxis {...axis} {...yearAxisProps(bvs)} /><YAxis {...axis} tickFormatter={fmtCurrencyCompact} width={50} />
              <Line dataKey="borrow" stroke={COLORS.borrow} strokeWidth={2} dot={false} isAnimationActive={false} />
              <Line dataKey="sell" stroke={COLORS.sell} strokeWidth={2} dot={false} isAnimationActive={false} />
            </LineChart>
          </section>

          <section className="chart-block">
            <h2>Loan-to-value</h2>
            <LineChart width={W} height={H} data={ltv}>
              <XAxis {...axis} {...yearAxisProps(ltv)} /><YAxis {...axis} tickFormatter={(v: number) => `${v}%`} width={36} />
              <ReferenceLine y={100} stroke={COLORS.danger} strokeDasharray="4 2" />
              <Line dataKey="ltv" stroke={COLORS.ltv} dot={false} connectNulls isAnimationActive={false} />
            </LineChart>
          </section>

          <section className="chart-block">
            <h2>Estate — value to heirs</h2>
            <BarChart width={W} height={H} data={estateData}>
              <XAxis dataKey="name" {...axis} /><YAxis {...axis} tickFormatter={fmtCurrencyCompact} width={50} />
              <Bar dataKey="value" isAnimationActive={false}>
                <Cell fill={COLORS.borrow} /><Cell fill={COLORS.sell} />
              </Bar>
            </BarChart>
          </section>

          <section className="chart-block">
            <h2>Cumulative interest</h2>
            <AreaChart width={W} height={H} data={cumulativeInterest}>
              <XAxis {...axis} {...yearAxisProps(cumulativeInterest)} /><YAxis {...axis} tickFormatter={fmtCurrencyCompact} width={50} />
              <Area dataKey="total" stroke={COLORS.interest} fill={COLORS.interest} fillOpacity={0.2} isAnimationActive={false} />
            </AreaChart>
          </section>

          {mcResult && (
            <section className="print-section">
              <h2>Monte Carlo summary</h2>
              <p>Runs: {mcResult.runs} · Maintenance-call probability: {fmtRatioPct(mcResult.maintenanceCallProbability)}</p>
              <p>Terminal net worth — p5 {fmtCurrency(mcResult.terminal.netWorth.p5)}, p50 {fmtCurrency(mcResult.terminal.netWorth.p50)}, p95 {fmtCurrency(mcResult.terminal.netWorth.p95)}</p>
            </section>
          )}

          <section className="print-break-before">
            <h2>Month-by-month detail</h2>
            <MonthTable rows={rows} />
          </section>
        </>
      )}
    </div>
  );
}

function MonthTable({ rows }: { rows: MonthlyRow[] }) {
  return (
    <table>
      <thead>
        <tr>
          <th>Mo</th><th>Date</th><th>Base</th><th>APR</th><th>Draw</th><th>Pmt</th><th>Interest</th>
          <th>Balance</th><th>Portfolio</th><th>Net worth</th><th>LVC</th><th>LTV</th><th>Cushion</th><th>Call</th>
        </tr>
      </thead>
      <tbody>
        {rows.map((r) => (
          <tr key={r.monthIndex}>
            <td>{r.monthIndex}</td><td>{r.date}</td><td>{r.baseRate.toFixed(2)}</td><td>{r.apr.toFixed(2)}</td>
            <td>{Math.round(r.drawThisMonth)}</td><td>{Math.round(r.paymentThisMonth)}</td><td>{Math.round(r.interestThisMonth)}</td>
            <td>{Math.round(r.endBalance)}</td><td>{Math.round(r.portfolioValue)}</td><td>{Math.round(r.netWorth)}</td>
            <td>{Math.round(r.loanValueOfCollateral)}</td><td>{Number.isFinite(r.ltv) ? `${(r.ltv * 100).toFixed(0)}%` : 'n/a'}</td>
            <td>{(r.callCushionPct * 100).toFixed(0)}%</td><td>{r.maintenanceCall ? 'Y' : ''}</td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

function ComparisonSection({ selection, scenarios }: { selection: string[]; scenarios: ReturnType<typeof useStore.getState>['scenarios'] }) {
  const items = selection
    .map((id) => scenarios.find((s) => s.id === id))
    .filter((s): s is NonNullable<typeof s> => !!s)
    .map((s) => ({ name: s.name, result: project(s.inputs) }));
  return (
    <section className="print-section">
      <h2>Comparison</h2>
      <table>
        <thead>
          <tr><th>Scenario</th><th>Start APR</th><th>Eff. spread</th><th>Ending net worth</th><th>Total interest</th><th>Worst LTV</th><th>Min cushion</th><th>Estate diff</th></tr>
        </thead>
        <tbody>
          {items.map((i) => (
            <tr key={i.name}>
              <td>{i.name}</td><td>{fmtPct(i.result.summary.startApr)}</td><td>{fmtPct(i.result.summary.effectiveSpread)}</td>
              <td>{fmtCurrency(i.result.summary.endingNetWorth)}</td><td>{fmtCurrency(i.result.summary.totalInterest)}</td>
              <td>{fmtRatioPct(i.result.summary.worstLtv)}</td><td>{fmtRatioPct(i.result.summary.minCallCushionPct)}</td>
              <td>{fmtCurrency(i.result.estate.difference)}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </section>
  );
}
